import "./upload-video.scss";
import { useEffect, useRef, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import Button from '@mui/material/Button';
import UPLOAD_SMALL from "../../assets/images/upload-small.svg";
import ICON_FILM from "../../assets/images/film.svg";
import { ref, uploadBytesResumable, UploadTask } from "firebase/storage";
import LoadingComponent from "../../components/loading/loading.component";
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import MediaService from "../../services/media.service";
import { v7 as uuidv7 } from 'uuid';
import { useSnackbar } from "../../utility/snackbar.utility";
import CustomSnackbar from "../../components/snackbar/snackbar.component";
import { MAX_DURATION, MAX_FILE_SIZE, VALID_MIME_TYPES } from "../../config/video-upload.config";
import { storage } from "../../firebase/auth.firebase";
import { MediaUsage } from "../../model/media-usage.model";
import { addLog } from "../../utility/add-log.utility";
import CircularProgress from "@mui/material/CircularProgress";

const UploadVideoModal = ({ show, onClose }) => {

    const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

    const inputImageRef = useRef<HTMLInputElement>(null);
    const uploadTaskRef = useRef<UploadTask | null>(null);
    const isMobile = (navigator as any).userAgentData?.mobile ||
        /Mobi|Android/i.test(navigator.userAgent) ||
        navigator.maxTouchPoints > 1;

    const [videoTitle, setVideoTitle] = useState("");
    const [videoDescription, setVideoDescription] = useState("");
    const [fileUpload, setFileUpload] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const [isShowError, setIsShowError] = useState(false);
    const [usage, setUsage] = useState<MediaUsage | null>(null);
    const [mediaDuration, setMediaDuration] = useState(0);
    const [isLoadingPopup, setIsLoadingPopup] = useState(true);

    useEffect(() => {
        fetchUsage();
    }, []);

    useEffect(() => {
        setIsDisabled(!(videoTitle && fileUpload && !isShowError));
    }, [videoTitle, fileUpload, isShowError]);

    const fetchUsage = async () => {
        setIsLoadingPopup(true);
        try {
            const response = await MediaService.getUsage();
            setUsage(response.data);
            setIsShowError(response.data.total_usage_minutes >= response.data.max_limit_minutes);
            setIsLoadingPopup(false);
        } catch {
            showSnackbar("", "error");
            onClose(false);
        }
    };

    const handleClose = () => {
        onClose(false);
    }

    const handleButtonClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        inputImageRef.current?.click();
    };

    const validateFile = (file: File) => {
        return new Promise((resolve, reject) => {
            if (!VALID_MIME_TYPES.includes(file.type)) {
                return reject('ประเภทไฟล์ไม่ถูกต้อง กรุณาเลือกไฟล์ที่มีนามสกุล .mp3, .mp4, .m4a, .mov หรือ .wav');
            }

            if (file.size > MAX_FILE_SIZE) {
                return reject('ขนาดไฟล์เกิน 20 GB กรุณาเลือกไฟล์ที่มีขนาดเล็กกว่า');
            }

            const mediaElement = file.type.startsWith('audio')
                ? new Audio()
                : document.createElement('video');

            mediaElement.src = URL.createObjectURL(file);

            mediaElement.onloadedmetadata = () => {
                const duration = mediaElement.duration;
                if (duration > MAX_DURATION) {
                    return reject('ไฟล์มีความยาวเกินไป กรุณาเลือกไฟล์ที่มีความยาวน้อยกว่า 2 ชั่วโมง');
                }

                const minutesDuration = Math.round(duration / 60);
                setMediaDuration(minutesDuration);
                const remainingDuration = (usage?.max_limit_minutes ?? 0) - (usage?.total_usage_minutes ?? 0);

                setIsShowError(minutesDuration > remainingDuration);
                resolve(true);
            };

            mediaElement.onerror = () => {
                return reject('ไม่สามารถโหลดไฟล์ได้ กรุณาลองใหม่อีกครั้ง');
            };
        });
    };

    const handleFileSelection = async (file: File) => {
        try {
            await validateFile(file);
            setFileUpload(file);
        } catch (error) {
            showSnackbar(error as string, "error");
        }
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        const file = event.target.files?.[0];
        if (file) {
            handleFileSelection(file);
            if (inputImageRef.current) inputImageRef.current.value = "";
        }
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const file = event.dataTransfer.files?.[0];
        if (file) handleFileSelection(file);
    };

    const handleDeleteFile = (event) => {
        event.stopPropagation();
        setFileUpload(null);
        setIsShowError(false);
    }

    const handleSubmit = async () => {
        if (!fileUpload || !videoTitle || isShowError) return;

        const uuid = uuidv7();
        const fileType = fileUpload.name.split('.').pop() || "";
        const fileName = `${uuid}.${fileType}`;

        try {
            setIsLoading(true);
            await addLog("UPLOAD_FILE", {
                new_data: {
                    file_name: fileName,
                    file_size: fileUpload.size,
                    file_duration: mediaDuration,
                },
            });

            const { data: usageData } = await MediaService.getUsage();
            const remainingDuration = usageData.max_limit_minutes - usageData.total_usage_minutes;

            if (mediaDuration > remainingDuration) {
                setIsShowError(true);
                return;
            }

            await uploadFile(fileUpload, fileName);
            await MediaService.addMedia(uuid, videoTitle, videoDescription, fileUpload.name);

            onClose(true);
        } catch (error) {
            handleUploadError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUploadError = async (error: any) => {
        const errorData = {
            code: error.code || '',
            message: error.message || '',
            name: error.name || '',
        };
        await addLog("UPLOAD_VIDEO_FILE_ERROR", { error: errorData });
        setProgress(0);

        const isCanceled = error?.code === 'storage/canceled';
        if (!isCanceled) {
            showSnackbar('', "error");
        }
    };

    const uploadFile = (file: File, fileName: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            const FILE_STORAGE = 'vdo_files/';
            const storageRef = ref(storage, `${FILE_STORAGE}${fileName}`);
            const uploadTask = uploadBytesResumable(storageRef, file);
            uploadTaskRef.current = uploadTask;

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setProgress(progress);
                },
                (error) => {
                    reject(error);
                },
                () => {
                    resolve();
                }
            );
        });
    };

    const handleCloseModal = () => {
        if (uploadTaskRef.current) {
            uploadTaskRef.current.cancel();
        }
        setIsLoading(false);
        setProgress(0);
    };

    return (
        <>
            <Modal show={show} onHide={handleClose} backdrop={isLoading ? "static" : true} centered className={isMobile ? 'upload-video-mobile-modal' : 'upload-video-modal'}>
                <Modal.Body className="upload-video-control">
                    {
                        isLoadingPopup ? (
                            <div className="upload-video-loading-control">
                                <CircularProgress />
                            </div>
                        ) : !isLoading ?
                            <>
                                <FontAwesomeIcon icon={faClose} className="upload-video-close-button" onClick={handleClose}></FontAwesomeIcon>
                                <span className="upload-video-label">อัปโหลดวิดีโอ/ไฟล์เสียง สำหรับสร้าง Infographic</span>

                                <Form className="upload-video-form-control">
                                    <Form.Group>
                                        <Form.Control
                                            type="text"
                                            placeholder="ตั้งชื่อวิดีโอ/ไฟล์เสียง*"
                                            value={videoTitle}
                                            className="upload-video-form-input"
                                            onChange={(e) => setVideoTitle(e.target.value)}
                                        />
                                    </Form.Group>

                                    <Form.Group>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            className="upload-video-form-input"
                                            placeholder="รายละเอียดสั้นๆ เกี่ยวกับวิดีโอ/ไฟล์เสียง"
                                            value={videoDescription}
                                            onChange={(e) => setVideoDescription(e.target.value)}
                                        />
                                    </Form.Group>

                                    <div
                                        className="upload-video-select-control"
                                        onDragOver={(event) => event.preventDefault()}
                                        onDrop={handleDrop}
                                        onClick={handleButtonClick}
                                    >
                                        <div>
                                            <div className='upload-video-select-header mb-1'>
                                                <img src={fileUpload ? ICON_FILM : UPLOAD_SMALL} className='me-2' width={16} height={16} />

                                                <span className='upload-video-select-text-title'>
                                                    {
                                                        fileUpload ?
                                                            <>
                                                                <span className="active">{fileUpload?.name}</span>
                                                                <IconButton color="primary" className="icon-close" onClick={handleDeleteFile}>
                                                                    <CloseIcon />
                                                                </IconButton>
                                                            </> : 'ลาก และ วางไฟล์ที่นี่ หรือ กดอัปโหลดไฟล์'
                                                    }
                                                </span>
                                            </div>

                                            <p className='upload-video-select-text-desc mb-2'>
                                                ไฟล์ที่รองรับ: .mp3, .mp4, .m4a, .mov, .wav <br />
                                                ขนาดไม่เกิน 20 GB ความยาวไม่เกิน 2ชม.
                                            </p>

                                            <p className='upload-video-select-text-warning'>
                                                กรุณาเลือกไฟล์เสียงคุณภาพดี เสียงชัด ไม่มีเสียงรบกวน <br />
                                                เพื่อผลลัพธ์การถอดเสียงที่แม่นยำ
                                            </p>
                                        </div>

                                        <input
                                            type="file"
                                            accept=".mp3, .mp4, .m4a, .mov, .wav"
                                            ref={inputImageRef}
                                            className='d-none'
                                            onChange={(event) => handleFileInputChange(event)}
                                        />

                                        <Button variant="outlined" color="primary" className="button-upload-file" onClick={handleButtonClick}>
                                            อัปโหลดวิดีโอ/ไฟล์เสียง
                                        </Button>
                                    </div>
                                </Form>

                                {
                                    isShowError &&
                                    <p className="error-content">
                                        ไม่สามารถอัปโหลดไฟล์ได้เนื่องจากเกินจำนวนนาทีที่อัปโหลดได้ต่อเดือน กรุณาติดต่อแอดมิน
                                    </p>
                                }
                                <Button
                                    variant="contained"
                                    className="upload-video-submit"
                                    onClick={handleSubmit}
                                    disabled={isDisabled}
                                >
                                    ดำเนินการต่อ
                                </Button>
                            </> :
                            <>
                                <LoadingComponent
                                    isShowProgressBar={true}
                                    isShowCancelButton={true}
                                    progress={progress}
                                    message={'ระบบกำลังทำการอัปโหลดวิดีโอ <br/> อาจใช้เวลาสักครู่กรุณาอย่าปิดหน้าต่างนี้'}
                                    onClose={handleCloseModal}
                                />
                            </>
                    }

                </Modal.Body>
            </Modal>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                duration={snackbar.duration}
                onClose={hideSnackbar}
            />
        </>
    )
}

export default UploadVideoModal;
